import { Modifier, StrictModifiers } from "@popperjs/core/lib/types";
import randomString from "../../helpers/randomString";
import * as React from 'react';
import { createPortal } from "react-dom";
import { Transition } from 'react-transition-group';
import classnames from "classnames";
import { ModifierPhases, Options, Placement } from "@popperjs/core";
import * as Popper from "@popperjs/core";
import { TransitionStatus } from "react-transition-group/Transition";
import { DropdownContext, DropdownContextType } from "./DropdownContext";
import style from './Dropdown.scss';
import { useSkipMountEffect } from "../../helpers/useSkipMountEffect";
import { useRefValue } from "../../helpers/useRefValue";
import { useClickOutside } from "../../helpers/useClickOutside";
import dispatchCloseEvent from "../Modals/helpers/dispatchCloseEvent";
import { defaultDropdownModifiers, sameWidthDropdownModifiers } from "./helpers/modifiers";

export function elementHasClass(element: HTMLElement, classes: string[]) {
  const classList = element.classList;
  for (let i = 0; i < classes.length; i++) {
    if (classList.contains(classes[i])) return true;
  }
  return false;
}

export function elementParentHasClass(element: HTMLElement, ignoreClasses: string[]) {
  while (element.parentElement) {
    if (element && elementHasClass(element, ignoreClasses)) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}

const transitionTimeout = { enter: 0, exit: 150 };

const popperConfig = (placement: Placement, modifiers: Partial<Modifier<any, any>>[]): Options => ({
  placement,
  strategy:  'absolute',
  modifiers: modifiers,
});

export type DropdownChildrenRenderProps = {
  onActivate: () => any,
  onDeactivate: () => any,
  elementRef: React.RefObject<any>,
  active: boolean,
};

export type Props = {
  children: (props: DropdownChildrenRenderProps) => JSX.Element,
  renderDropdown: () => JSX.Element,
  dropdownClassName?: string,
  onDeactivate?: () => any,
  sameWidth?: boolean,
  placement?: Placement,
  modifiers?: (Modifier<any, any> | StrictModifiers)[],
};

export type DropdownApi = {
  activate: () => any,
  deactivate: (event?: MouseEvent) => any,
}

const Dropdown = React.forwardRef<DropdownApi, Props>((props, ref) => {
  const { children, renderDropdown, dropdownClassName } = props;
  const dropdownContext = React.useContext(DropdownContext);
  const propsRef = useRefValue(props);

  const ignoreClass = React.useMemo(() => randomString(20), []);
  const containerRef = React.useRef<any>();
  const dropdownRef = React.useRef<HTMLDivElement>();
  const popperRef = React.useRef<Popper.Instance>();
  const ignoreClassNamesRef = React.useRef<string[]>([ignoreClass]);

  const [visible, setVisible] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const activeRef = useRefValue(active);
  const disableRequestedRef = React.useRef(false);

  React.useEffect(() => {
    return () => {
      const popper = popperRef.current;
      if (popper) popper.destroy();
    }
  }, [])

  React.useEffect(() => {
    if (dropdownContext) dropdownContext.addChildren(ignoreClass);
    return () => {
      if (dropdownContext) dropdownContext.removeChildren(ignoreClass);
    }
  }, [dropdownContext]);

  useSkipMountEffect(() => {
    const { onDeactivate } = propsRef.current;
    if (!active && onDeactivate) onDeactivate();
  }, [active]);

  useSkipMountEffect(() => {
    if (visible) {
      const { sameWidth, placement, modifiers } = propsRef.current;
      popperRef.current = Popper.createPopper(
        containerRef.current,
        dropdownRef.current,
        popperConfig(placement || 'bottom-start', modifiers || (sameWidth ? sameWidthDropdownModifiers : defaultDropdownModifiers)),
      );
    } else {
      const popper = popperRef.current;
      if (popper) {
        popper.destroy();
        popperRef.current = null;
      }
    }
  }, [visible]);

  const activate = React.useCallback(() => {
    disableRequestedRef.current = false;
    if (popperRef.current) {
      if (popperRef.current.state.elements.reference !== containerRef.current) {
        popperRef.current.state.elements.reference = containerRef.current;
        popperRef.current.update();
      }
    }
    if (activeRef.current) return;
    setVisible(true);
    setActive(true)
  }, [])

  const deactivate = React.useCallback((event?: MouseEvent) => {
    disableRequestedRef.current = true;
    requestAnimationFrame(() => {
      if (!disableRequestedRef.current) return;
      if (!activeRef.current) return;

      if (event) {
        const el = event.target as HTMLElement;
        if (elementParentHasClass(el, ignoreClassNamesRef.current)) return;
      }

      dispatchCloseEvent();
      setActive(false);
      disableRequestedRef.current = false;
    })
  }, [])

  useClickOutside(
    containerRef,
    deactivate,
    { ignoreClass },
  );

  const onExited = React.useCallback(() => setVisible(false), []);

  const contextProvider: DropdownContextType = React.useMemo(() => ({
    addChildren:    (className: string) => {
      if (dropdownContext) dropdownContext.addChildren(className);
      const ignoreClassNames = ignoreClassNamesRef.current;
      if (!ignoreClassNames.includes(className)) ignoreClassNamesRef.current = [...ignoreClassNames, className];
    },
    removeChildren: (className: string) => {
      if (dropdownContext) dropdownContext.removeChildren(className);
      const ignoreClassNames = ignoreClassNamesRef.current;
      if (ignoreClassNames.includes(className)) ignoreClassNamesRef.current = ignoreClassNames.filter(c => c !== className);
    },
  }), [dropdownContext]);

  React.useImperativeHandle(ref, () => ({ activate, deactivate }), [activate, deactivate]);

  const renderSelfDropdown = React.useCallback((state: TransitionStatus) => {
    const className = classnames(
      ignoreClass,
      style.dropdown,
      state === 'entered' && style.dropdownActive,
      dropdownClassName,
    );

    return createPortal((
      <div ref={dropdownRef} className={className}>
        <div className={style.arrow} data-popper-arrow />
        {renderDropdown()}
      </div>
    ), document.body);
  }, [renderDropdown, dropdownClassName]);

  return (
    <DropdownContext.Provider value={contextProvider}>
      {children({
        onActivate:   activate,
        onDeactivate: deactivate,
        elementRef:   containerRef,
        active,
      })}
      {visible && (
        <Transition in={active} timeout={transitionTimeout} appear={true} onExited={onExited}>
          {renderSelfDropdown}
        </Transition>
      )}
    </DropdownContext.Provider>
  )
});

export default Dropdown;
